import { useState, Component } from "react";
import { pricingAPI, mapsAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  Calculator,
  Sparkles,
  Loader2,
  ArrowRight,
  MapPin,
  Clock,
  Lock,
  AlertCircle
} from "lucide-react";
import { cn } from "../lib/utils";

const VAT_RATE = 0.16;

// Error Boundary específico para RoutePriceCalculator
class CalculatorErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error.message || "Error en el calculador" };
  }

  componentDidCatch(error, info) {
    console.error("[RoutePriceCalculator] Error caught:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-amber-800">
            <AlertCircle className="w-5 h-5" />
            <p className="text-sm font-medium">El calculador de ruta no está disponible</p>
          </div>
          <p className="text-xs text-amber-600 mt-1">Puede continuar capturando el precio manualmente.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * RoutePriceCalculator - Componente reutilizable para calcular rutas y precios
 * 
 * Props:
 * - origin: string - Dirección de origen
 * - destination: string - Dirección de destino
 * - originLat: number - Latitud de origen (opcional)
 * - originLng: number - Longitud de origen (opcional)
 * - destLat: number - Latitud de destino (opcional)
 * - destLng: number - Longitud de destino (opcional)
 * - vehicleType: string - Tipo de vehículo (default: "sedan")
 * - passengers: number - Número de pasajeros
 * - serviceType: string - Tipo de servicio
 * - currency: string - Moneda del documento ("MXN" | "USD")
 * - exchangeRate: number - Tipo de cambio (si currency === "USD")
 * - requiresInvoice: boolean - Si requiere factura (para IVA)
 * - salesOwnerId: string - ID del vendedor dueño del documento
 * - onApplySuggestion: function - Callback cuando se aplica la sugerencia
 *   Recibe: { basePrice, subtotalMXN, distanceKm, durationMin, routeSource, rulesSnapshot }
 * - disabled: boolean - Deshabilitar el botón de cálculo
 */
function RoutePriceCalculatorInner({
  origin,
  destination,
  originLat,
  originLng,
  destLat,
  destLng,
  vehicleType = "sedan",
  passengers = 1,
  serviceType = "transfer",
  currency = "MXN",
  exchangeRate = null,
  requiresInvoice = false,
  salesOwnerId,
  onApplySuggestion,
  disabled = false
}) {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [error, setError] = useState(null);

  // RBAC: Solo Admin o el dueño del documento puede aplicar la sugerencia
  const canEditPricing = isAdmin || (salesOwnerId && user?.id === salesOwnerId);

  // Verificar si se puede calcular
  const canCalculate = origin && destination && origin.trim() !== "" && destination.trim() !== "";

  const handleCalculate = async () => {
    if (!canCalculate) {
      toast.error("Ingrese origen y destino para calcular la ruta");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Geocodificar direcciones si no tenemos coordenadas
      let finalOriginLat = originLat;
      let finalOriginLng = originLng;
      let finalDestLat = destLat;
      let finalDestLng = destLng;

      // Geocodificar origen si no tiene coordenadas
      if (!finalOriginLat || !finalOriginLng) {
        try {
          const originGeoRes = await mapsAPI.geocode(null, origin);
          if (originGeoRes.data?.lat && originGeoRes.data?.lng) {
            finalOriginLat = originGeoRes.data.lat;
            finalOriginLng = originGeoRes.data.lng;
          }
        } catch (geoErr) {
          console.warn("Could not geocode origin:", geoErr);
        }
      }

      // Geocodificar destino si no tiene coordenadas
      if (!finalDestLat || !finalDestLng) {
        try {
          const destGeoRes = await mapsAPI.geocode(null, destination);
          if (destGeoRes.data?.lat && destGeoRes.data?.lng) {
            finalDestLat = destGeoRes.data.lat;
            finalDestLng = destGeoRes.data.lng;
          }
        } catch (geoErr) {
          console.warn("Could not geocode destination:", geoErr);
        }
      }

      // Step 2: Calcular precio con coordenadas (si las tenemos)
      const res = await pricingAPI.suggestPrice({
        origin,
        destination,
        origin_lat: finalOriginLat,
        origin_lng: finalOriginLng,
        dest_lat: finalDestLat,
        dest_lng: finalDestLng,
        vehicle_type: vehicleType,
        passengers: parseInt(passengers, 10) || 1,
        service_type: serviceType
      });

      const data = res.data;
      setSuggestion(data);

      // Mostrar mensaje según resultado
      if (data.pricing_method === "fixed_route") {
        toast.success(`✅ Ruta fija: ${data.pricing_details?.fixed_route_name} - $${data.suggested_price?.toLocaleString("es-MX")}`);
      } else if (data.pricing_method === "distance_calculation" && data.distance_km) {
        toast.success(`✅ Ruta calculada: ${data.distance_km} km - Precio: $${data.suggested_price?.toLocaleString("es-MX")}`);
      } else if (data.suggested_price) {
        toast.info(`💡 Precio sugerido: $${data.suggested_price?.toLocaleString("es-MX")}`);
      } else {
        toast.warning("No se pudo calcular un precio automático. Ingrese el precio manualmente.");
        setError(data.pricing_details?.message || "Sin datos de tarifa disponibles");
      }
    } catch (err) {
      console.error("Error calculating route:", err);
      // Manejar diferentes formatos de error de respuesta
      let errorMsg = "Error al calcular la ruta. Verifique las direcciones.";
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        // Si detail es un array de errores de validación (FastAPI)
        if (Array.isArray(detail)) {
          errorMsg = detail.map(d => d.msg || d.message || JSON.stringify(d)).join(", ");
        } else if (typeof detail === 'object') {
          // Si es un objeto, extraer el mensaje
          errorMsg = detail.msg || detail.message || JSON.stringify(detail);
        } else {
          errorMsg = String(detail);
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!suggestion?.suggested_price) return;
    if (!canEditPricing) {
      toast.error("No tiene permiso para aplicar el precio sugerido");
      return;
    }

    // Calcular precio en la moneda del documento
    let basePrice = suggestion.suggested_price;
    let subtotalMXN = basePrice;

    // Si el documento es en USD, convertir el precio sugerido (que viene en MXN)
    if (currency === "USD" && exchangeRate && exchangeRate > 0) {
      basePrice = Math.round((suggestion.suggested_price / exchangeRate) * 100) / 100;
      subtotalMXN = suggestion.suggested_price;
    }

    // Determinar route_source
    let routeSource = "MANUAL";
    if (suggestion.pricing_method === "fixed_route") {
      routeSource = "FIXED_ROUTE";
    } else if (suggestion.pricing_method === "distance_calculation") {
      // Verificar si usó Google Maps o Haversine
      routeSource = suggestion.route_source || "GOOGLE";
    }

    onApplySuggestion?.({
      basePrice,
      suggestedPrice: suggestion.suggested_price,
      subtotalMXN,
      distanceKm: suggestion.distance_km,
      durationMin: suggestion.duration_minutes,
      routeSource,
      rulesSnapshot: suggestion.rules_snapshot,
      pricingMethod: suggestion.pricing_method
    });

    toast.success("Precio sugerido aplicado");
  };

  // Calcular preview en la moneda del documento
  const getPreviewPrice = () => {
    if (!suggestion?.suggested_price) return null;
    
    if (currency === "USD" && exchangeRate && exchangeRate > 0) {
      return Math.round((suggestion.suggested_price / exchangeRate) * 100) / 100;
    }
    return suggestion.suggested_price;
  };

  // Calcular IVA y total para preview
  const getPreviewTotal = () => {
    const base = getPreviewPrice();
    if (!base) return null;
    
    if (requiresInvoice) {
      const vat = base * VAT_RATE;
      return { subtotal: base, vat, total: base + vat };
    }
    return { subtotal: base, vat: 0, total: base };
  };

  const previewPrice = getPreviewPrice();
  const previewTotal = getPreviewTotal();
  const currencySymbol = currency === "USD" ? "USD $" : "$";

  return (
    <div className="space-y-3" data-testid="route-price-calculator">
      {/* Botón de cálculo */}
      <Button
        type="button"
        variant="outline"
        onClick={handleCalculate}
        disabled={disabled || loading || !canCalculate}
        className={cn(
          "w-full gap-2",
          canCalculate ? "border-blue-300 text-blue-700 hover:bg-blue-50" : "text-slate-400"
        )}
        data-testid="calculate-route-btn"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Calculator className="w-4 h-4" />
        )}
        {loading ? "Calculando..." : "Calcular Ruta y Precio"}
      </Button>

      {/* Mensaje si faltan campos */}
      {!canCalculate && (
        <p className="text-xs text-slate-500 text-center">
          Complete origen y destino para calcular
        </p>
      )}

      {/* Resultado de sugerencia */}
      {suggestion && (
        <Card className={cn(
          "transition-all",
          suggestion.suggested_price
            ? "bg-blue-50 border-blue-200"
            : "bg-amber-50 border-amber-200"
        )}>
          <CardContent className="p-4 space-y-3">
            {suggestion.suggested_price ? (
              <>
                {/* Header con precio sugerido */}
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-semibold text-blue-800">
                        Precio Sugerido
                      </p>
                      <p className="text-xs text-blue-600">
                        {suggestion.pricing_method === "fixed_route"
                          ? `Ruta fija: ${suggestion.pricing_details?.fixed_route_name}`
                          : suggestion.pricing_details?.message || "Cálculo por distancia"}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-700">
                      {currencySymbol}{previewPrice?.toLocaleString("es-MX")}
                    </p>
                    {currency === "USD" && (
                      <p className="text-xs text-blue-500">
                        MXN ${suggestion.suggested_price?.toLocaleString("es-MX")}
                      </p>
                    )}
                  </div>
                </div>

                {/* Detalles de ruta */}
                {(suggestion.distance_km || suggestion.duration_minutes) && (
                  <div className="flex items-center gap-4 text-sm text-blue-600">
                    {suggestion.distance_km && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {suggestion.distance_km} km
                      </span>
                    )}
                    {suggestion.duration_minutes && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {suggestion.duration_minutes} min
                      </span>
                    )}
                    <span className="text-xs bg-blue-100 px-2 py-0.5 rounded">
                      {suggestion.route_source || (suggestion.pricing_method === "fixed_route" ? "RUTA FIJA" : "GOOGLE")}
                    </span>
                  </div>
                )}

                {/* Preview de IVA (si aplica) */}
                {requiresInvoice && previewTotal && (
                  <div className="pt-2 border-t border-blue-200 text-sm space-y-1">
                    <div className="flex justify-between text-blue-600">
                      <span>Subtotal:</span>
                      <span>{currencySymbol}{previewTotal.subtotal?.toLocaleString("es-MX")}</span>
                    </div>
                    <div className="flex justify-between text-blue-600">
                      <span>IVA (16%):</span>
                      <span>{currencySymbol}{previewTotal.vat?.toLocaleString("es-MX")}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-blue-800">
                      <span>Total:</span>
                      <span>{currencySymbol}{previewTotal.total?.toLocaleString("es-MX")}</span>
                    </div>
                  </div>
                )}

                {/* Botón aplicar con RBAC */}
                <div className="pt-2">
                  {canEditPricing ? (
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleApply}
                      className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
                      data-testid="apply-suggestion-btn"
                    >
                      <ArrowRight className="w-4 h-4" />
                      Aplicar Sugerencia
                    </Button>
                  ) : (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="w-full">
                            <Button
                              type="button"
                              size="sm"
                              disabled
                              className="w-full gap-2 bg-slate-300 text-slate-500 cursor-not-allowed"
                              data-testid="apply-suggestion-btn-disabled"
                            >
                              <Lock className="w-4 h-4" />
                              Aplicar Sugerencia
                            </Button>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Solo Admin o Vendedor dueño puede aplicar cambios</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </>
            ) : (
              /* Sin sugerencia disponible */
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Sin sugerencia disponible</p>
                  <p className="text-sm text-amber-600">
                    {suggestion.pricing_details?.message || "Configure tarifas en Configuración → Precios"}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {error && !suggestion?.suggested_price && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-2 rounded">
          <AlertCircle className="w-4 h-4" />
          <span>{typeof error === 'string' ? error : JSON.stringify(error)}</span>
        </div>
      )}
    </div>
  );
}

// Export con Error Boundary wrapper
export default function RoutePriceCalculator(props) {
  return (
    <CalculatorErrorBoundary>
      <RoutePriceCalculatorInner {...props} />
    </CalculatorErrorBoundary>
  );
}
